/**
 * V320.RELOAD.5: User Gesture Detection
 * Prevents Chrome intervention warnings for vibrate/audio APIs
 * by gating them behind actual user interactions
 */

class UserGestureDetector {
  private hasUserGesture = false;
  private listeners: Array<() => void> = [];

  constructor() {
    this.setupListeners();
  }

  private setupListeners() {
    // One-time listeners for user gestures
    const gestureEvents = ['pointerdown', 'keydown', 'click', 'touchstart'];
    
    const handleGesture = () => {
      if (!this.hasUserGesture) {
        this.hasUserGesture = true;
        console.log('✅ V320.RELOAD.5: User gesture detected - vibrate/audio enabled');
        
        // Notify listeners
        this.listeners.forEach(listener => listener());
        
        // Remove listeners after first gesture
        gestureEvents.forEach(event => {
          window.removeEventListener(event, handleGesture, { capture: true });
        });
      }
    };

    // Attach listeners with capture phase to catch early
    gestureEvents.forEach(event => {
      window.addEventListener(event, handleGesture, { capture: true, passive: true });
    });
  }

  /**
   * Check if user has performed a gesture
   */
  public hasGesture(): boolean {
    return this.hasUserGesture;
  }

  /**
   * Register callback to be notified when first gesture occurs
   */
  public onFirstGesture(callback: () => void): void {
    if (this.hasUserGesture) {
      callback();
    } else {
      this.listeners.push(callback);
    }
  }

  /**
   * Safe vibrate - only if user has gestured
   */
  public safeVibrate(pattern: number | number[]): boolean {
    if (!this.hasUserGesture) {
      console.log('⚠️ V320.RELOAD.5: Skipping vibrate - no user gesture yet');
      return false;
    }

    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
        return true;
      } catch (error) {
        console.warn('⚠️ V320.RELOAD.5: Vibrate failed:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Safe audio context resume - only if user has gestured
   */
  public async safeResumeAudioContext(audioContext: AudioContext): Promise<boolean> {
    if (!this.hasUserGesture) {
      console.log('⚠️ V320.RELOAD.5: Skipping audio resume - no user gesture yet');
      return false;
    }

    try {
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        return true;
      }
      return true;
    } catch (error) {
      console.warn('⚠️ V320.RELOAD.5: Audio resume failed:', error);
      return false;
    }
  }
}

// Singleton instance
export const userGestureDetector = new UserGestureDetector();

